import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientViewsService } from '../services/api';
import { AppNav } from '../components/AppNav';
import { NoAgencyBanner } from '../components/NoAgencyBanner';
import { Share2, ChevronRight, Plus, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ClientViewSummary {
  id: string;
  title: string;
  created_at: string;
  items: Array<{ id: string; property_id: string }>;
  notes: unknown[];
}

function ClientViewsPage() {
  const navigate = useNavigate();
  const [views, setViews] = useState<ClientViewSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [noAgency, setNoAgency] = useState(false);

  useEffect(() => {
    document.title = 'Selecciones — Takarah';
    clientViewsService.list()
      .then(setViews)
      .catch((err) => {
        if (err?.response?.status === 403) {
          setNoAgency(true);
        } else {
          setError('No se pudieron cargar las selecciones');
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (noAgency) return (
    <div className="min-h-[100dvh] bg-background">
      <AppNav />
      <NoAgencyBanner />
    </div>
  );

  return (
    <div className="min-h-[100dvh] bg-background">
      <AppNav />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Selecciones</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {loading ? '—' : `${views.length} ${views.length !== 1 ? 'selecciones' : 'selección'} ${views.length !== 1 ? 'compartidas' : 'compartida'}`}
            </p>
          </div>
          <Button size="sm" onClick={() => navigate('/properties')} className="gap-1.5">
            <Plus className="w-4 h-4" />
            Nueva
          </Button>
        </div>

        {error && <p className="text-sm text-destructive mb-4">{error}</p>}

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-muted rounded-xl animate-pulse" />
            ))}
          </div>
        ) : views.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-4">
              <Share2 className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="font-medium">Sin selecciones aún</p>
            <p className="text-sm text-muted-foreground mt-1 mb-4 max-w-xs">
              Busca propiedades, selecciónalas y crea una selección para compartir con un cliente.
            </p>
            <Button onClick={() => navigate('/properties')} size="sm">Buscar propiedades</Button>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {views.map((v) => (
              <button
                key={v.id}
                onClick={() => navigate(`/client-views/${v.id}`)}
                className="w-full text-left flex items-center gap-4 py-4 px-3 -mx-3 rounded-xl hover:bg-muted/30 transition-colors group"
              >
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Eye className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{v.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {v.items.length} propiedad{v.items.length !== 1 ? 'es' : ''} ·{' '}
                    {new Date(v.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground shrink-0" />
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default ClientViewsPage;
